export type VaultEntryKind = "secret" | "config";

export interface VaultReferenceContext {
  serverId?: string;
}

export interface VaultReferenceProvider {
  resolveValue: (
    kind: VaultEntryKind,
    key: string,
    context?: VaultReferenceContext,
  ) => Promise<string>;
}

export interface VaultScope {
  type: "global" | "server";
  serverId?: string;
}

export interface VaultEntrySummary {
  id: string;
  key: string;
  kind: VaultEntryKind;
  scope: VaultScope;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface VaultTemplateMatch {
  raw: string;
  kind: VaultEntryKind;
  key: string;
}

const TEMPLATE_PATTERN = /{{\s*(secret|config)\s*:\s*([^}]+?)\s*}}/g;

export function hasVaultTemplate(value: string): boolean {
  TEMPLATE_PATTERN.lastIndex = 0;
  return TEMPLATE_PATTERN.test(value);
}

export function findVaultTemplateMatches(value: string): VaultTemplateMatch[] {
  TEMPLATE_PATTERN.lastIndex = 0;
  const matches: VaultTemplateMatch[] = [];

  for (const match of value.matchAll(TEMPLATE_PATTERN)) {
    const kind = match[1];
    const key = match[2]?.trim();
    const raw = match[0];
    if ((kind === "secret" || kind === "config") && key && raw) {
      matches.push({ raw, kind, key });
    }
  }

  return matches;
}

export async function resolveVaultTemplateString(
  value: string,
  provider?: VaultReferenceProvider,
  context?: VaultReferenceContext,
): Promise<string> {
  const matches = findVaultTemplateMatches(value);
  if (matches.length === 0) return value;
  if (!provider) {
    throw new Error("This value references secrets/configs, but no vault provider is available");
  }

  let resolved = value;
  for (const match of matches) {
    const replacement = await provider.resolveValue(match.kind, match.key, context);
    resolved = resolved.replace(match.raw, replacement);
  }

  return resolved;
}

export async function resolveVaultTemplateRecord(
  record: Record<string, string>,
  provider?: VaultReferenceProvider,
  context?: VaultReferenceContext,
): Promise<Record<string, string>> {
  const resolved: Record<string, string> = {};

  for (const [key, value] of Object.entries(record)) {
    const resolvedKey = await resolveVaultTemplateString(key, provider, context);
    const resolvedValue = await resolveVaultTemplateString(value, provider, context);
    resolved[resolvedKey] = resolvedValue;
  }

  return resolved;
}

export function createVaultScriptHelpers(
  provider?: VaultReferenceProvider,
  context?: VaultReferenceContext,
): {
  secret: (key: string) => Promise<string>;
  config: (key: string) => Promise<string>;
} {
  const resolve = async (kind: VaultEntryKind, key: string) => {
    if (!provider) {
      throw new Error(`${kind}() is not available in this execution context`);
    }
    return provider.resolveValue(kind, key, context);
  };

  return {
    secret: (key) => resolve("secret", key),
    config: (key) => resolve("config", key),
  };
}
